import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw, Save, X } from 'lucide-react';
import { PARTIES_THEME } from './partiesTheme';
import PartyFormPanel from './PartyFormPanel';

export default function PartyFormModal({
  isOpen,
  onClose,
  formSectionRef,
  editingPartyId,
  formProps,
}) {
  const theme = PARTIES_THEME;
  const isEdit = Boolean(editingPartyId);
  const formId = 'party-form-modal';

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={theme.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="party-form-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close"
        onClick={onClose}
      />
      <div ref={formSectionRef} className={theme.modalPanel}>
        <div className={`flex shrink-0 items-center justify-between gap-3 ${theme.panelHeader} px-5 py-4`}>
          <div>
            <h3 id="party-form-modal-title" className="text-lg font-bold text-slate-900">
              {isEdit ? 'Edit party' : 'Add new party'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Press ESC to close</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors touch-manipulation"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          <PartyFormPanel
            {...formProps}
            embedded
            inModal
            formId={formId}
            hideFormActions
            editingPartyId={editingPartyId}
          />
        </div>

        <div className="shrink-0 flex flex-col-reverse sm:flex-row gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={formProps.clearPartyForm}
            className={`sm:w-auto w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold bg-white border rounded-xl ${theme.btnSecondary}`}
          >
            <RotateCcw className="h-4 w-4 shrink-0" aria-hidden />
            {isEdit ? 'Cancel' : 'Clear'}
          </button>
          <button
            type="submit"
            form={formId}
            className={`sm:flex-1 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl bg-gradient-to-r ${theme.btn} shadow-sm`}
          >
            <Save className="h-4 w-4 shrink-0" aria-hidden />
            {isEdit ? 'Update party' : 'Save party'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
